import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import inboundWebhookService from '../api/services/inboundWebhookService';
import { formSchemaService } from '../api';
import { Layout } from '../components/Layout';
import { Button, Input, ThemedText } from '../components';
import { PageHeaderRow } from '../components/PageStyles';
import { ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter } from '../components/ModalStyles';
import { FormGroup, FormLabel, StyledSelect } from '../components/FormStyles';
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from '../components/TableStyles';
import { ToggleSwitch, ToggleInput, ToggleSlider } from '../components/ToggleStyles';
import { CodeExampleViewer } from '../components/CodeExampleViewer';
import { generateCodeExamples, buildSamplePayload, formatJson } from '../utils/webhookCodeExamples';

// ─── Styled Components ──────────────────────────────────────────
const PageContainer = styled.div`max-width: 900px; margin: 0 auto;`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background: none;
  border: none;
  color: ${props => props.theme.colors.tint};
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.default};
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const FormCard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm || '0 1px 3px rgba(0,0,0,0.08)'};
`;

const Section = styled.div`margin-top: ${({ theme }) => theme.spacing.xl};`;

const SectionTitle = styled.h3`
  font-size: 0.85rem; font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  text-transform: uppercase; letter-spacing: 0.5px;
`;

const TokenDisplay = styled.div`
  display: flex; align-items: center; gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const TokenCode = styled.code`flex: 1; word-break: break-all; font-size: 0.85rem;`;

const InfoText = styled.p`
  font-size: 0.8rem; color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const DeliveryBadge = styled.span`
  display: inline-block; padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.75rem; font-weight: 600;
  background: ${({ $status, theme }) =>
    $status === 'Success' ? theme.colors.success + '20' :
    $status === 'Failed' ? theme.colors.danger + '20' : theme.colors.warning + '20'};
  color: ${({ $status, theme }) =>
    $status === 'Success' ? theme.colors.success :
    $status === 'Failed' ? theme.colors.danger : theme.colors.warning};
`;

const PaginationRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm};
  font-size: 0.8rem; color: ${({ theme }) => theme.colors.textSecondary};
`;

const RequiredBadge = styled.span`
  display: inline-block; padding: 1px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 0.7rem; font-weight: 600;
  background: ${({ $required, theme }) =>
    $required ? theme.colors.primary + '15' : theme.colors.border + '40'};
  color: ${({ $required, theme }) =>
    $required ? theme.colors.primary : theme.colors.textSecondary};
`;

const Footer = styled.div`
  display: flex; justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

// ─── Component ──────────────────────────────────────────────────
const DELIVERY_PAGE_SIZE = 10;
const LAYOUT_FIELD_TYPES = ['Divider', 'Header', 'Spacer', 'RepeatedForm', 'SectionBreak'];

const InboundWebhookEndpointPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [endpoint, setEndpoint] = useState(null);
  const [formSchemas, setFormSchemas] = useState([]);
  const [name, setName] = useState('');
  const [formSchemaId, setFormSchemaId] = useState('');
  const [description, setDescription] = useState('');
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState(100);
  const [isActive, setIsActive] = useState(true);
  const [showTokenOnce, setShowTokenOnce] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [deliveryPage, setDeliveryPage] = useState(1);
  const [deliveryTotal, setDeliveryTotal] = useState(0);
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [schemaFields, setSchemaFields] = useState([]);
  const [loadingSchema, setLoadingSchema] = useState(false);

  const prevAutoNameRef = useRef('');

  useEffect(() => {
    const init = async () => {
      try {
        const schemas = await formSchemaService.getFormSchemas({ pageSize: 200 });
        const schemaList = schemas?.items || schemas || [];
        setFormSchemas(Array.isArray(schemaList) ? schemaList : []);
        if (isEdit) {
          const ep = await inboundWebhookService.getEndpointById(id);
          if (ep) {
            setEndpoint(ep);
            setName(ep.name || '');
            setFormSchemaId(ep.formSchemaId || '');
            setDescription(ep.description || '');
            setRateLimitPerMinute(ep.rateLimitPerMinute ?? 100);
            setIsActive(ep.isActive);
          } else {
            toast.error('Endpoint not found');
            navigate('/settings?tab=integrations&sub=inbound');
            return;
          }
        }
      } catch { toast.error('Failed to load data'); }
      setLoading(false);
    };
    init();
  }, [id, isEdit, navigate]);

  useEffect(() => {
    if (!formSchemaId || isEdit) return;
    const selectedSchema = formSchemas.find(s => String(s.id) === String(formSchemaId));
    if (!selectedSchema) return;

    const autoName = `${selectedSchema.name} Webhook`;
    const prevAutoName = prevAutoNameRef.current;

    if (!name.trim() || name === prevAutoName) {
      setName(autoName);
    }
    prevAutoNameRef.current = autoName;
  }, [formSchemaId, formSchemas, isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!formSchemaId) {
      setSchemaFields([]);
      return;
    }
    let cancelled = false;
    const loadSchema = async () => {
      setLoadingSchema(true);
      try {
        const schemaResult = await formSchemaService.getFormSchemaById(formSchemaId);
        const raw = schemaResult.schemaDefinition ?? schemaResult.formSchema ?? '{}';
        const parsed = typeof raw === 'string' ? JSON.parse(raw || '{}') : (raw || {});
        const allFields = parsed.fields || [];
        const webhookFields = allFields
          .filter(f => !LAYOUT_FIELD_TYPES.includes(f.type) && !f.isHidden && !f.hidden)
          .map(f => ({
            key: f.key || f.id,
            title: f.title || f.label || f.key || f.id,
            type: f.type || 'text',
            required: !!f.required,
            options: f.configuration?.options || f.options || [],
          }));
        if (!cancelled) setSchemaFields(webhookFields);
      } catch {
        if (!cancelled) setSchemaFields([]);
      }
      if (!cancelled) setLoadingSchema(false);
    };
    loadSchema();
    return () => { cancelled = true; };
  }, [formSchemaId]);

  const loadDeliveries = useCallback(async (page = 1) => {
    if (!isEdit) return;
    try {
      const result = await inboundWebhookService.getDeliveries({
        endpointId: id, page, pageSize: DELIVERY_PAGE_SIZE,
      });
      setDeliveries(result?.items || []);
      setDeliveryTotal(result?.total || 0);
      setDeliveryPage(page);
    } catch { /* delivery log is supplemental */ }
  }, [id, isEdit]);

  useEffect(() => {
    if (isEdit && !loading) loadDeliveries(1);
  }, [isEdit, loading, loadDeliveries]);

  const handleSave = async () => {
    if (!name.trim()) { toast.warning('Name is required'); return; }
    if (!formSchemaId) { toast.warning('Please select a form schema'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        formSchemaId: parseInt(formSchemaId, 10),
        description: description.trim() || null,
        rateLimitPerMinute, isActive,
      };
      if (isEdit) {
        await inboundWebhookService.updateEndpoint(id, payload);
        toast.success('Endpoint updated');
        navigate('/settings?tab=integrations&sub=inbound');
      } else {
        const result = await inboundWebhookService.createEndpoint(payload);
        if (result?.token) {
          setShowTokenOnce(result.token);
          setEndpoint(result);
          toast.success('Endpoint created');
        } else {
          toast.success('Endpoint created');
          navigate('/settings?tab=integrations&sub=inbound');
        }
      }
    } catch (err) { toast.error(err?.message || 'Failed to save endpoint'); }
    setSaving(false);
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const result = await inboundWebhookService.regenerateToken(id);
      if (result?.token) { setShowTokenOnce(result.token); toast.success('Token regenerated'); }
    } catch { toast.error('Failed to regenerate token'); }
    setRegenerating(false);
    setShowRegenModal(false);
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await inboundWebhookService.testEndpoint(id);
      toast.success('Test payload sent successfully');
      loadDeliveries(deliveryPage);
    } catch { toast.error('Test failed'); }
    setTesting(false);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    try { return new Date(dateStr).toLocaleString(); } catch { return dateStr; }
  };

  const totalDeliveryPages = Math.ceil(deliveryTotal / DELIVERY_PAGE_SIZE);

  const webhookUrl = showTokenOnce
    ? `${window.location.origin}/api/webhook/inbound/${showTokenOnce}`
    : `${window.location.origin}/api/webhook/inbound/{your-token}`;
  const codeExamples = generateCodeExamples({ webhookUrl, fields: schemaFields });
  const sampleBody = schemaFields.length > 0
    ? formatJson(buildSamplePayload(schemaFields))
    : formatJson({ fields: { field_key: "value" } });

  if (loading) {
    return (
      <Layout>
        <PageContainer theme={theme}>
          <p style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.textSecondary }}>
            Loading...
          </p>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageContainer theme={theme}>
        <PageHeaderRow theme={theme}>
          <div>
            <BackButton theme={theme} onClick={() => navigate('/settings?tab=integrations&sub=inbound')}>
              <ArrowLeft size={14} /> Back to Webhooks
            </BackButton>
            <ThemedText type="title" style={{ marginTop: 8 }}>
              {isEdit ? 'Edit Inbound Webhook' : 'New Inbound Webhook'}
            </ThemedText>
          </div>
          {isEdit && (
            <Button variant="secondary" size="sm" onClick={handleTest} disabled={testing}>
              {testing ? 'Sending...' : 'Send Test'}
            </Button>
          )}
        </PageHeaderRow>

        <FormCard theme={theme}>
          <SectionTitle theme={theme}>Configuration</SectionTitle>

          <Input
            label="Name"
            required
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="My Inbound Webhook"
          />

          <FormGroup theme={theme}>
            <FormLabel theme={theme}>Form Schema *</FormLabel>
            <StyledSelect theme={theme} value={formSchemaId} onChange={e => setFormSchemaId(e.target.value)} disabled={isEdit}>
              <option value="">Select a form schema</option>
              {formSchemas.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </StyledSelect>
            {isEdit && <InfoText theme={theme}>Form schema cannot be changed after creation.</InfoText>}
          </FormGroup>

          {formSchemaId && (
            <FormGroup theme={theme} style={{ marginTop: theme.spacing.lg }}>
              <FormLabel theme={theme}>Sample Request Body</FormLabel>
              <InfoText theme={theme} style={{ marginTop: 0, marginBottom: theme.spacing.sm }}>
                POST this JSON to the webhook URL. Field keys come from your form schema.
              </InfoText>
              {loadingSchema ? (
                <InfoText theme={theme}>Loading schema fields...</InfoText>
              ) : (
                <CodeExampleViewer
                  snippets={[{ label: 'JSON', language: 'json', code: sampleBody }]}
                  defaultTab="JSON"
                />
              )}
            </FormGroup>
          )}

          <Input
            label="Description"
            type="textarea"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Optional description"
          />

          <Input
            label="Rate Limit (requests per minute)"
            type="number"
            min={1}
            max={10000}
            value={rateLimitPerMinute}
            onChange={e => setRateLimitPerMinute(parseInt(e.target.value, 10) || 100)}
          />

          {isEdit && (
            <FormGroup theme={theme}>
              <FormLabel theme={theme}>Status</FormLabel>
              <ToggleSwitch theme={theme}>
                <ToggleInput type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                <ToggleSlider theme={theme} $checked={isActive} />
                <span style={{ fontSize: '0.9rem', color: theme.colors.text }}>{isActive ? 'Active' : 'Inactive'}</span>
              </ToggleSwitch>
            </FormGroup>
          )}

          <Footer theme={theme}>
            <Button variant="secondary" onClick={() => navigate('/settings?tab=integrations&sub=inbound')}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update Endpoint' : 'Create Endpoint'}
            </Button>
          </Footer>
        </FormCard>

        {isEdit && (
          <Section theme={theme}>
            <FormCard theme={theme}>
              <SectionTitle theme={theme}>Authentication Token</SectionTitle>
              <TokenDisplay theme={theme}>
                <TokenCode>{showTokenOnce || endpoint?.maskedToken || '••••••••••••'}</TokenCode>
                {showTokenOnce ? (
                  <Button variant="secondary" size="sm" onClick={() => { navigator.clipboard.writeText(showTokenOnce); toast.success('Token copied!'); }}>
                    Copy
                  </Button>
                ) : (
                  <InfoText theme={theme} style={{ margin: 0 }}>Token was shown only on creation</InfoText>
                )}
              </TokenDisplay>
              <div style={{ marginTop: theme.spacing.md }}>
                <Button variant="danger" size="sm" onClick={() => setShowRegenModal(true)}>Regenerate Token</Button>
              </div>
              {showTokenOnce && (
                <InfoText theme={theme} style={{ color: theme.colors.warning, fontWeight: 500 }}>
                  Copy this token now. It will not be shown again.
                </InfoText>
              )}
            </FormCard>
          </Section>
        )}

        {isEdit && (
          <Section theme={theme}>
            <FormCard theme={theme}>
              <SectionTitle theme={theme}>Integration Guide</SectionTitle>

              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Webhook URL</FormLabel>
                <TokenDisplay theme={theme}>
                  <TokenCode>{`${window.location.origin}/api/webhook/inbound/{token}`}</TokenCode>
                  <Button variant="secondary" size="sm" onClick={() => {
                    navigator.clipboard.writeText(webhookUrl);
                    toast.success('URL copied!');
                  }}>Copy</Button>
                </TokenDisplay>
              </FormGroup>

              <InfoText theme={theme}>
                Use the <code>X-Idempotency-Key</code> header for duplicate prevention.
              </InfoText>

              <FormGroup theme={theme} style={{ marginTop: theme.spacing.md }}>
                <FormLabel theme={theme}>Code Examples</FormLabel>
                {loadingSchema ? (
                  <InfoText theme={theme}>Loading schema fields...</InfoText>
                ) : (
                  <CodeExampleViewer snippets={codeExamples} defaultTab="cURL" />
                )}
              </FormGroup>

              {schemaFields.length > 0 && (
                <FormGroup theme={theme} style={{ marginTop: theme.spacing.md }}>
                  <FormLabel theme={theme}>Field Reference</FormLabel>
                  <TableContainer theme={theme}>
                    <Table theme={theme}>
                      <Thead theme={theme}>
                        <tr>
                          <Th theme={theme}>Key</Th>
                          <Th theme={theme}>Title</Th>
                          <Th theme={theme}>Type</Th>
                          <Th theme={theme}>Required</Th>
                        </tr>
                      </Thead>
                      <Tbody>
                        {schemaFields.map(field => (
                          <Tr key={field.key} theme={theme}>
                            <Td theme={theme}><code style={{ fontSize: '0.8rem' }}>{field.key}</code></Td>
                            <Td theme={theme}>{field.title}</Td>
                            <Td theme={theme}>{field.type}</Td>
                            <Td theme={theme}>
                              <RequiredBadge theme={theme} $required={field.required}>
                                {field.required ? 'Yes' : 'No'}
                              </RequiredBadge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </FormGroup>
              )}

              <InfoText theme={theme}>
                Replace <code>{'{token}'}</code> with your authentication token and use
                the field keys from your form schema as the JSON keys.
              </InfoText>
            </FormCard>
          </Section>
        )}

        {isEdit && (
          <Section theme={theme}>
            <FormCard theme={theme} style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: theme.spacing.lg, paddingBottom: 0 }}>
                <SectionTitle theme={theme}>Recent Deliveries</SectionTitle>
              </div>
              {deliveries.length === 0 ? (
                <p style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.textSecondary, fontSize: '0.85rem' }}>
                  No deliveries recorded yet.
                </p>
              ) : (
                <>
                  <TableContainer theme={theme} style={{ border: 'none', borderRadius: 0 }}>
                    <Table theme={theme}>
                      <Thead theme={theme}>
                        <tr>
                          <Th theme={theme}>Time</Th>
                          <Th theme={theme}>Status</Th>
                          <Th theme={theme}>Source IP</Th>
                          <Th theme={theme}>Submission ID</Th>
                          <Th theme={theme}>Duration</Th>
                        </tr>
                      </Thead>
                      <Tbody>
                        {deliveries.map(d => (
                          <Tr key={d.id} theme={theme}>
                            <Td theme={theme}>{formatTime(d.receivedAt || d.createdAt)}</Td>
                            <Td theme={theme}>
                              <DeliveryBadge theme={theme} $status={d.status}>{d.status}</DeliveryBadge>
                            </Td>
                            <Td theme={theme}>{d.sourceIp || '—'}</Td>
                            <Td theme={theme}>{d.submissionId || '—'}</Td>
                            <Td theme={theme}>{d.durationMs != null ? `${d.durationMs}ms` : '—'}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                  {totalDeliveryPages > 1 && (
                    <PaginationRow theme={theme}>
                      <span>Page {deliveryPage} of {totalDeliveryPages} ({deliveryTotal} total)</span>
                      <div style={{ display: 'flex', gap: theme.spacing.xs }}>
                        <Button variant="secondary" size="sm" disabled={deliveryPage <= 1} onClick={() => loadDeliveries(deliveryPage - 1)}>Previous</Button>
                        <Button variant="secondary" size="sm" disabled={deliveryPage >= totalDeliveryPages} onClick={() => loadDeliveries(deliveryPage + 1)}>Next</Button>
                      </div>
                    </PaginationRow>
                  )}
                </>
              )}
            </FormCard>
          </Section>
        )}

        {showRegenModal && (
          <ModalOverlay $isOpen theme={theme}>
            <ModalContent theme={theme} style={{ maxWidth: 450 }}>
              <ModalHeader theme={theme}><h3>Regenerate Token</h3></ModalHeader>
              <ModalBody theme={theme}>
                <p>This will invalidate the current token immediately. Any integrations using the old token will stop working.</p>
                <p style={{ fontWeight: 500, marginTop: theme.spacing.sm }}>Are you sure you want to continue?</p>
              </ModalBody>
              <ModalFooter theme={theme}>
                <Button variant="secondary" onClick={() => setShowRegenModal(false)}>Cancel</Button>
                <Button variant="danger" onClick={handleRegenerate} disabled={regenerating}>
                  {regenerating ? 'Regenerating...' : 'Regenerate'}
                </Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}

        {showTokenOnce && !isEdit && (
          <ModalOverlay $isOpen theme={theme}>
            <ModalContent theme={theme} style={{ maxWidth: 500 }}>
              <ModalHeader theme={theme}><h3>Inbound Webhook Token</h3></ModalHeader>
              <ModalBody theme={theme}>
                <p style={{ color: theme.colors.warning, fontWeight: 500, marginBottom: theme.spacing.sm }}>
                  Copy this token now. It will not be shown again.
                </p>
                <TokenDisplay theme={theme}>
                  <TokenCode>{showTokenOnce}</TokenCode>
                  <Button variant="secondary" size="sm" onClick={() => { navigator.clipboard.writeText(showTokenOnce); toast.success('Token copied!'); }}>Copy</Button>
                </TokenDisplay>
                <InfoText theme={theme}>Use this token in your webhook URL to authenticate inbound submissions.</InfoText>
              </ModalBody>
              <ModalFooter theme={theme}>
                <Button onClick={() => { setShowTokenOnce(null); navigate('/settings?tab=integrations&sub=inbound'); }}>Done</Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </PageContainer>
    </Layout>
  );
};

export { InboundWebhookEndpointPage };
export default InboundWebhookEndpointPage;
